import { gql } from "@apollo/client";

const TARGETS_QUERY = gql`
  query aliveTargets{
    aliveTargets {
      _id
      droneType
      serialNumber
      freq_l
      freq_h
      alive
      locked
      latestLocation {
        latitude
        longitude
        height
        speed
        created_at
        directions {
          sensor {
            _id
          }
          azimuth
          elevation
        }
        tdoaData {
          sensor1 {
            _id
          }
          sensor2 {
            _id
          }
          tdoa
        }
        detections {
          sensor {
            _id
          }
          lastSeenAt
          hardware_center_freq_hz
          decode_target_freq_hz
          ant_idx
        }
        detectMode
        timeout
        inBoundary
        shouldAlarm
        whitelistedInRedZone
      }
    }
  }
`;

const TARGETS_QUERY_BY_ID = gql`
  query TargetById($targetId: ID!) {
    targetById(targetID: $targetId) {
      _id
      droneType
      serialNumber
      freq_l
      freq_h
      alive
      locked
      latestLocation {
        latitude
        longitude
        height
        speed
        created_at
        directions {
          azimuth
          elevation
          sensor {
            _id
          }
        }
        tdoaData {
          sensor1 {
            _id
          }
          sensor2 {
            _id
          }
          tdoa
        }
      }
    }
  }
`;

const TARGETS_SUBSCRIPTION = gql`
  subscription Subscription {
    targetUpdated {
      target {
        _id
        droneType
        serialNumber
        freq_l
        freq_h
        alive
        locked
      }
      location {
        latitude
        longitude
        height
        speed
        created_at
        directions {
          sensor {
            _id
          }
          azimuth
          elevation
        }
        tdoaData {
          sensor1 {
            _id
          }
          sensor2 {
            _id
          }
          tdoa
        }
        detections {
          sensor {
            _id
          }
          lastSeenAt
          hardware_center_freq_hz
          decode_target_freq_hz
          ant_idx
        }
        detectMode
        timeout
        inBoundary
        shouldAlarm
        whitelistedInRedZone
      }
    }
  }
`;

const PAST_TARGET = gql`
  query pastTargets($startDate: String, $endDate: String) {
    pastTargets(startDate: $startDate, endDate: $endDate) {
      _id
      droneType
      created_at
      updated_at
    }
  }
`;

const TARGET_HISTORY_BY_ID = gql`
  query Query($targetId: ID!, $time: String) {
    targetById(targetID: $targetId) {
      droneType
      _id
      freq_l
      freq_h
      alive
      created_at
      history {
        _id
        latitude
        longitude
        height
        created_at
        directions {
          sensor {
            _id
            sensorLocation(time: $time) {
              latitude
              longitude
              referenceAngle
              antennas
              differenceAngle
              created_at
            }
          }
          azimuth
          elevation
        }
      }
    }
  }
`;

const LOCATIONS = gql`
  query Locations($startTime: String, $endTime: String) {
    Locations(startTime: $startTime, endTime: $endTime) {
      _id
      whose {
        _id
      }
      latitude
      longitude
      height
      created_at
      directions {
        latitude
        longitude
        referenceAngle
        azimuth
        elevation
      }
    }
  }
`;

const SENSOR_INFO_SUMMARY = gql`
  fragment SensorInfoSummary on SensorInfo {
    updatedAt
    switch_ants
    hopping_channels
    container_name
    cpuTemperature
    sa_diag {
      type
      connected
    }
    fem_diag {
      connected
    }
    system_resources {
      cpu_percent
      memory_percent
      ssd_used_gb
      ssd_total_gb
      ssd_percent
    }
    errMessage
  }
`;

const MONITOR_STATUS_SUMMARY = gql`
  fragment MonitorStatusSummary on MonitorStatus {
    _id
    sensor {
      _id
    }
    schema_version
    station_id
    boot_id
    sequence
    generated_at
    observed_at
    system
    containers
    software
    collection_errors
    last_received_at
    monitorState
  }
`;

const SENSOR_UPDATED = gql`
  subscription Subscription {
    sensorInClusterUpdated {
      sensorConfiguration {
        sensorLocation {
          latitude
          longitude
          altitude
          referenceAngle
        }
      sensorInfo {
        ...SensorInfoSummary
      }
      }
      monitorStatus {
        ...MonitorStatusSummary
      }
      sensorType
      status
      locationName
      apiKey
      _id
      tag
      show
      cluster {
        name
      }
    }
  }
  ${SENSOR_INFO_SUMMARY}
  ${MONITOR_STATUS_SUMMARY}
`;

const SENSORS_QUERY = gql`
  query SensorsInCluster {
    sensorsInCluster { 
      sensorConfiguration {
        sensorLocation {
          latitude
          longitude
          altitude
          referenceAngle
        }
        sensorInfo {
          ...SensorInfoSummary
        }
      }
      monitorStatus {
        ...MonitorStatusSummary
      }
      cluster {
        name
      }
      apiKey
      sensorType
      _id
      status
      locationName
      tag
      show
    }
  }
  ${SENSOR_INFO_SUMMARY}
  ${MONITOR_STATUS_SUMMARY}
`;

const POLL_PLAYBACK_TARGET_WITH_TIME = gql`
  query Query(
    $endDate: String
    $startDate: String
    $endTime: String
    $time: String
  ) {
    pastTargets(endDate: $endDate, startDate: $startDate) {
      sensors {
        _id
        tag
        sensorConfiguration {
          sensorLocation(time: $time) {
            latitude
            longitude
            altitude
            referenceAngle
          }
          sensorInfo(time: $time) {
            ...SensorInfoSummary
          }
        }
        sensorType
        cluster {
          name
        }
        status
        locationName
        show
      }
      targets {
        history(endTime: $endTime) {
          directions {
            azimuth
            elevation
            sensor {
              _id
            }
          }
          tdoaData {
            sensor1 {
              _id
            }
            sensor2 {
              _id
            }
            tdoa
          }
          detections {
            sensor {
              _id
            }
            lastSeenAt
            hardware_center_freq_hz
            decode_target_freq_hz
            ant_idx
          }
          latitude
          longitude
          height
          speed
          created_at
          detectMode
          inBoundary
          shouldAlarm
          whitelistedInRedZone
          timeout
        }
        _id
        droneType
        serialNumber
        freq_h
        freq_l
        created_at
        updated_at
        clusters {
          name
        }
      }
      jammers {
        _id
        device_id
        cluster {
          _id
          name
        }
        display_name
        device_type
        lifecycle_status
        retired_at
        retired_by
        retirement_reason
        app_version
        os_version
        kernel_version
        config_version
        system_boot_id
        controller_instance_id
        boot_id
        controller_boot_id
        controller_state
        antenna_type
        jamming_enabled
        active_bands
        band_capabilities
        system
        network
        services
        state_sequence
        control_lease
        position
        availability
        last_command_id
        last_error_code
        latest_state_at
        latest_telemetry_at
        latest_event_at
        latest_availability_at
        latest_config_reported_at
        last_seen_at
        projection_ordering
        created_at
        updated_at
        details {
          cluster_name
          lifecycle_status
          retired_at
          retired_by
          retirement_reason
          app_version
          os_version
          kernel_version
          config_version
          system_boot_id
          controller_instance_id
          boot_id
          controller_boot_id
          controller_state
          jamming_enabled
          active_bands
          band_capabilities
          system
          network
          services
          state_sequence
          control_lease
          availability
          last_command_id
          last_error_code
          latest_state_at
          latest_telemetry_at
          latest_event_at
          latest_availability_at
          latest_config_reported_at
          last_seen_at
          projection_ordering
        }
      }
    }
  }
  ${SENSOR_INFO_SUMMARY}
`;

const GET_PAST_TARGETS_LIST = gql`
  query Query($date: String, $limit: Int, $includeLicense: Boolean) {
    pastTargetsList(
      date: $date
      limit: $limit
      includeLicense: $includeLicense
    ) {
      droneType
      firstLocation {
        latitude
        longitude
        created_at
      }
      _id
    }
  }
`;

const COMMAND_SENSOR = gql`
  mutation commanSensor(
    $msg: String!
    $type: String!
    $apiKey: String!
    $sensorType: String!
  ) {
    commandSensor(
      msg: $msg
      type: $type
      apiKey: $apiKey
      sensorType: $sensorType
    ) {
      status
      msg
      type
    }
  }
`;

const UPDATE_SENSOR_CONFIGURATION = gql`
  mutation updateSensorConfiguration(
    $sensorType: String!
    $apiKey: String!
    $sensorConfigurationInput: UpdateSensorConfigurationInput!
  ) {
    updateSensorConfiguration(
      sensorType: $sensorType
      apiKey: $apiKey
      sensorConfigurationInput: $sensorConfigurationInput
    )
  }
`;

const DELETE_SENSOR = gql`
  mutation DeleteSensor($sensorId: ID!) {
    deleteSensor(sensorId: $sensorId)
  }
`;

const UPDATE_TARGET_ARBITRARY = gql`
  mutation Mutation(
    $target: UpdateTargetInput!
    $location: CreateLocationInput
  ) {
    updateTarget_Arbitrary(target: $target, location: $location)
  }
`;

const CREATE_TARGET_ARBITRARY = gql`
  mutation Mutation($data: CreateTargetInput!) {
    createTarget_Arbitrary(data: $data) {
      _id
    }
  }
`;

const TOGGLE_TARGET_LOCKED = gql`
  mutation Mutation($toggleTargetLockedId: String, $locked: Boolean) {
    toggleTargetLocked(id: $toggleTargetLockedId, locked: $locked) {
      _id
      locked
    }
  }
`;

const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!, $remember: Boolean, $force: Boolean) {
    login(email: $email, password: $password, remember: $remember, force: $force) {
      status
      msg
      user {
        authority
        cluster {
          _id
          name
          center {
            latitude
            longitude
          } 
        }
        verifiedEmail
        verified
        myPermissions
      }
    }
  }
`;

const LOGOUT = gql`
  mutation Logout {
    logout
  }
`;

const ME = gql`
  query Me {
    me {
      id
      authority
      cluster {
        name
        center {
          latitude
          longitude
        }
        _id
      }
      clusters {
        _id
        name
      }
      verifiedEmail
      verified
      myPermissions
    }
  }
`;

const CHANGE_MAIN_CLUSTER = gql`
  mutation ChangeMainCluster($clusterName: String!) {
    changeMainCluster(clusterName: $clusterName)
  }
`;

const UPDATE_USER_CLUSTERS = gql`
  mutation UpdateUserClusters($userId: ID!, $clusterIds: [ID!]!) {
    updateUserClusters(userId: $userId, clusterIds: $clusterIds) {
      id
      clusters {
        _id
        name
      }
    }
  }
`;

const REQUEST_EMAIL_VERIFICATION = gql`
  mutation RequestEmailVerification($email: String!) {
    requestEmailVerification(email: $email)
  }
`;

const VERIFY_EMAIL = gql`
  mutation VerifyEmail($email: String!, $code: String!) {
    verifyEmail(email: $email, code: $code) {
      id
      authority
      cluster {
        name
        center {
          latitude
          longitude
        }
      }
      myPermissions
      verifiedEmail
      verified
    }
  }
`;

const CLUSTER_QUERY = gql`
  query Cluster($name: String) {
    cluster(name: $name) {
      center {
        latitude
        longitude
      }
      name
      _id
    }
  }
`;

const ALL_CLUSTER_QUERY = gql`
query AllCluster {
  allCluster {
    name
    _id
    center {
      latitude
      longitude
    }
  }
}
`;

const UPDATE_PRESENCE_LOG_EXPORT_SETTINGS = gql`
mutation UpdatePresenceLogExportSettings(
  $enabled: Boolean!
  $simpleFields: [String!]!
  $allowLicenseInSimpleExport: Boolean!
) {
  updatePresenceLogExportSettings(
    enabled: $enabled
    simpleFields: $simpleFields
    allowLicenseInSimpleExport: $allowLicenseInSimpleExport
  ) {
    clusterName
    presenceLogExport {
      enabled
      simpleFields
      allowLicenseInSimpleExport
    }
  }
}
`;

const POLICESTATION_QUERY = gql`
query AllPoliceStation {
  allPoliceStation {
    _id
    latitude
    longitude
    name
    phone
    address
    policePhone {
      name
      phone
    }
  }
}
`;

const POLICE_STATION_ADMIN_FIELDS = `
  _id
  cluster { _id name }
  latitude
  longitude
  name
  phone
  address
  policePhone { name phone }
  created_at
  updated_at
`;

const ADMIN_POLICE_STATIONS_QUERY = gql`
query AdminPoliceStations($clusterId: ID!) {
  adminPoliceStations(clusterId: $clusterId) { ${POLICE_STATION_ADMIN_FIELDS} }
}
`;

const CREATE_POLICE_STATION = gql`
mutation CreatePoliceStation($clusterId: ID!, $data: PoliceStationDataInput!) {
  createPoliceStation(clusterId: $clusterId, data: $data) { ${POLICE_STATION_ADMIN_FIELDS} }
}
`;

const UPDATE_POLICE_STATION = gql`
mutation UpdatePoliceStation($clusterId: ID!, $stationId: ID!, $data: PoliceStationDataInput!) {
  updatePoliceStation(clusterId: $clusterId, stationId: $stationId, data: $data) {
    ${POLICE_STATION_ADMIN_FIELDS}
  }
}
`;

const DELETE_POLICE_STATION = gql`
mutation DeletePoliceStation($clusterId: ID!, $stationId: ID!) {
  deletePoliceStation(clusterId: $clusterId, stationId: $stationId)
}
`;

// ---- 管理後台：cluster 與紅區（boundary）維護 ----
const ADMIN_BOUNDARIES_QUERY = gql`
query AdminBoundaries($clusterId: ID) {
  adminBoundaries(clusterId: $clusterId) {
    _id
    clusterId
    clusterName
    name
    shapeKind
    polygon { lat lng }
    holes { lat lng }
    center { lat lng }
    radius
    alarm
    display
    color
    enabled
    created_at
    updated_at
  }
}
`;

const CREATE_CLUSTER = gql`
mutation CreateCluster($name: String!, $latitude: String!, $longitude: String!) {
  createCluster(name: $name, latitude: $latitude, longitude: $longitude) {
    _id
    name
    center { latitude longitude }
  }
}
`;

const UPDATE_CLUSTER_CENTER = gql`
mutation UpdateClusterCenter($clusterId: ID!, $latitude: String!, $longitude: String!) {
  updateClusterCenter(clusterId: $clusterId, latitude: $latitude, longitude: $longitude) {
    _id
    name
    center { latitude longitude }
  }
}
`;

const BOUNDARY_ADMIN_FIELDS = `
  _id
  clusterId
  clusterName
  name
  shapeKind
  polygon { lat lng }
  holes { lat lng }
  center { lat lng }
  radius
  alarm
  display
  color
  enabled
  created_at
  updated_at
`;

const CREATE_BOUNDARY = gql`
mutation CreateBoundary($clusterId: ID!, $input: BoundaryInput!) {
  createBoundary(clusterId: $clusterId, input: $input) { ${BOUNDARY_ADMIN_FIELDS} }
}
`;

const UPDATE_BOUNDARY = gql`
mutation UpdateBoundary($boundaryId: ID!, $input: BoundaryInput!, $force: Boolean) {
  updateBoundary(boundaryId: $boundaryId, input: $input, force: $force) { ${BOUNDARY_ADMIN_FIELDS} }
}
`;

const SET_BOUNDARY_ENABLED = gql`
mutation SetBoundaryEnabled($boundaryId: ID!, $enabled: Boolean!, $force: Boolean) {
  setBoundaryEnabled(boundaryId: $boundaryId, enabled: $enabled, force: $force) { ${BOUNDARY_ADMIN_FIELDS} }
}
`;

const DELETE_BOUNDARY = gql`
mutation DeleteBoundary($boundaryId: ID!, $force: Boolean) {
  deleteBoundary(boundaryId: $boundaryId, force: $force)
}
`;

const SENSOR_MODE_SENSORS_QUERY = gql`
  query SensorModeSensors {
    sensorsInCluster {
      _id
      tag
      locationName
      sensorType
      sensorConfiguration {
        sensorLocation {
          latitude
          longitude
          referenceAngle
        }
      }
    }
  }
`;

const SENSOR_MODES_QUERY = gql`
  query SensorModes {
    sensorModes {
      _id
      number
      name
      enabled
      created_at
      updated_at
      stations {
        sensor { _id tag locationName sensorType }
        antennas
        bandsHz
        protocol
        location { latitude longitude referenceAngle }
        directions { antenna localAngle absoluteAngle selected }
      }
    }
  }
`;

const SENSOR_MODE_EXECUTIONS_QUERY = gql`
  query SensorModeExecutions($limit: Int) {
    sensorModeExecutions(limit: $limit) {
      _id
      mode_number
      mode_name
      combined
      status
      requested_by
      created_at
      sourceModes { _id number name }
      results {
        sensor { _id tag locationName }
        sensor_name
        tag
        protocol
        antennas
        bandsHz
        apiKeySha256_12
        commandId
        status
        error
      }
    }
  }
`;

const CREATE_SENSOR_MODE = gql`
  mutation CreateSensorMode($input: SensorModeInput!) {
    createSensorMode(input: $input) {
      _id number name enabled
    }
  }
`;

const UPDATE_SENSOR_MODE = gql`
  mutation UpdateSensorMode($modeId: ID!, $input: SensorModeInput!) {
    updateSensorMode(modeId: $modeId, input: $input) {
      _id number name enabled
    }
  }
`;

const DUPLICATE_SENSOR_MODE = gql`
  mutation DuplicateSensorMode($modeId: ID!, $number: String!, $name: String!) {
    duplicateSensorMode(modeId: $modeId, number: $number, name: $name) {
      _id number name enabled
    }
  }
`;

const SET_SENSOR_MODE_ENABLED = gql`
  mutation SetSensorModeEnabled($modeId: ID!, $enabled: Boolean!) {
    setSensorModeEnabled(modeId: $modeId, enabled: $enabled) {
      _id number name enabled
    }
  }
`;

const DELETE_SENSOR_MODE = gql`
  mutation DeleteSensorMode($modeId: ID!) {
    deleteSensorMode(modeId: $modeId)
  }
`;

const EXECUTE_SENSOR_MODE = gql`
  mutation ExecuteSensorMode($modeId: ID!, $clientRequestId: String) {
    executeSensorMode(modeId: $modeId, clientRequestId: $clientRequestId) {
      _id
      mode_number
      mode_name
      status
      results {
        sensor { _id tag locationName }
        protocol
        status
        error
        commandId
      }
    }
  }
`;

const EXECUTE_SENSOR_MODES = gql`
  mutation ExecuteSensorModes($modeIds: [ID!]!, $clientRequestId: String) {
    executeSensorModes(modeIds: $modeIds, clientRequestId: $clientRequestId) {
      _id
      mode_number
      mode_name
      combined
      status
      sourceModes { _id number name }
      results {
        sensor { _id tag locationName }
        protocol
        status
        error
      }
    }
  }
`;

const BOUNDARY_QUERY = gql`
query GetBoundary {
  getBoundary {
    id
    name
    zoneName
    kind
    polygon {
      lat
      lng
    }
    holes {
      lat
      lng
    }
    center {
      lat
      lng
    }
    radius
    alarm
    color
  }
}
`;

const GET_SETTING = gql`
query GetSetting {
  setting{
    clusterName
    onlyDevelopment
    unknownReceive
    basestationMonitor {
      unavailableColor
      metrics {
        cpuPercent {
          enabled
          warning
          critical
          normalColor
          warningColor
          criticalColor
        }
        memoryPercent {
          enabled
          warning
          critical
          normalColor
          warningColor
          criticalColor
        }
        ssdPercent {
          enabled
          warning
          critical
          normalColor
          warningColor
          criticalColor
        }
        cpuTemperature {
          enabled
          warning
          critical
          normalColor
          warningColor
          criticalColor
        }
      }
    }
    presenceLogExport {
      enabled
      simpleFields
      allowLicenseInSimpleExport
    }
  }
}
`;

const GET_PRESENCE_EXPORT_SETTING = gql`
query GetPresenceExportSetting {
  setting {
    clusterName
    presenceLogExport {
      enabled
      simpleFields
      allowLicenseInSimpleExport
    }
  }
}
`;

const UPDATE_BASESTATION_MONITOR_SETTINGS = gql`
mutation UpdateBasestationMonitorSettings($input: BasestationMonitorSettingsInput!) {
  updateBasestationMonitorSettings(input: $input) {
    clusterName
    basestationMonitor {
      unavailableColor
      metrics {
        cpuPercent { enabled warning critical normalColor warningColor criticalColor }
        memoryPercent { enabled warning critical normalColor warningColor criticalColor }
        ssdPercent { enabled warning critical normalColor warningColor criticalColor }
        cpuTemperature { enabled warning critical normalColor warningColor criticalColor }
      }
    }
  }
}
`;

const CHANGE_ONLYDEVELOPMENT = gql`
mutation ChangeOnlyDevelopmentStatus($status: Boolean) {
  changeOnlyDevelopmentStatus(status: $status){
    clusterName
    value
  }
}
`;

const JAMMER_QUERY = gql`
query GetJammers {
  getJammers {
    _id
    serial
    type
    jammerLocation {
      lat
      lon
      alt
      azi
      elv
    }
    jammerStatus {
      jamming
      band
      conn
      direction
      hw_status
      soft_ver
    }
  }
}
`;

const JAMMER_SUBSCRIPTION = gql`
subscription JammerUpdated {
  jammerUpdated {
    jammer {
      serial
      type
      _id
    }
    location {
      lat
      lon
      alt
      azi
      elv
    }
    status {
      jamming
      band
      conn
      direction
      soft_ver
      hw_status
    }
  }
}
`;

const JAMMER_CHECK_UPDATE = gql`
subscription Subscription {
  jammerCheckUpdated {
    status
    cmd_recv
  }
}
`;

const JAMMER_DEVICE_QUERY = gql`
query JammerDevices($clusterId: ID!) {
  jammerDevices(clusterId: $clusterId) {
    _id
    device_id
    cluster_id
    map_status
    cluster {
      _id
      name
    }
    display_name
    device_type
    lifecycle_status
    retired_at
    retired_by
    retirement_reason
    app_version
    os_version
    kernel_version
    config_version
    system_boot_id
    controller_instance_id
    boot_id
    controller_boot_id
    controller_state
    antenna_type
    jamming_enabled
    active_bands
    band_capabilities
    system
    network
    services
    state_sequence
    control_lease
    position
    availability
    last_command_id
    last_error_code
    latest_state_at
    latest_telemetry_at
    latest_event_at
    latest_availability_at
    latest_config_reported_at
    last_seen_at
    projection_ordering
    created_at
    updated_at
    details {
      cluster_name
      lifecycle_status
      retired_at
      retired_by
      retirement_reason
      app_version
      os_version
      kernel_version
      config_version
      system_boot_id
      controller_instance_id
      boot_id
      controller_boot_id
      controller_state
      jamming_enabled
      active_bands
      band_capabilities
      system
      network
      services
      state_sequence
      control_lease
      availability
      last_command_id
      last_error_code
      latest_state_at
      latest_telemetry_at
      latest_event_at
      latest_availability_at
      latest_config_reported_at
      last_seen_at
      projection_ordering
    }
  }
}
`;

const JAMMER_DEVICE_SUBSCRIPTION = gql`
subscription JammerDeviceUpdated($clusterId: ID!) {
  jammerV1DeviceUpdated(clusterId: $clusterId) {
    _id
    device_id
    cluster_id
    map_status
    cluster {
      _id
      name
    }
    display_name
    device_type
    lifecycle_status
    retired_at
    retired_by
    retirement_reason
    app_version
    os_version
    kernel_version
    config_version
    system_boot_id
    controller_instance_id
    boot_id
    controller_boot_id
    controller_state
    antenna_type
    jamming_enabled
    active_bands
    band_capabilities
    system
    network
    services
    state_sequence
    control_lease
    position
    availability
    last_command_id
    last_error_code
    latest_state_at
    latest_telemetry_at
    latest_event_at
    latest_availability_at
    latest_config_reported_at
    last_seen_at
    projection_ordering
    created_at
    updated_at
    details {
      cluster_name
      lifecycle_status
      retired_at
      retired_by
      retirement_reason
      app_version
      os_version
      kernel_version
      config_version
      system_boot_id
      controller_instance_id
      boot_id
      controller_boot_id
      controller_state
      jamming_enabled
      active_bands
      band_capabilities
      system
      network
      services
      state_sequence
      control_lease
      availability
      last_command_id
      last_error_code
      latest_state_at
      latest_telemetry_at
      latest_event_at
      latest_availability_at
      latest_config_reported_at
      last_seen_at
      projection_ordering
    }
  }
}
`;

const REGISTER_JAMMER_DEVICE = gql`
mutation RegisterJammerDevice($input: RegisterJammerDeviceInput!) {
  registerJammerDevice(input: $input) {
    _id
    device_id
    display_name
    cluster {
      _id
      name
    }
  }
}
`;

const JAMMER_CONFIG_QUERY = gql`
query JammerConfig($clusterId: ID!, $deviceId: String!) {
  jammerConfig(clusterId: $clusterId, deviceId: $deviceId) {
    _id
    device_id
    desired {
      message_id
      config_version
      status
      issued_at
      reported_at
      payload
      error
    }
    reported {
      message_id
      config_version
      status
      issued_at
      reported_at
      payload
      error
    }
    desired_updated_at
    reported_updated_at
    created_at
    updated_at
  }
}
`;

const SET_JAMMER_DESIRED_CONFIG = gql`
mutation SetJammerDesiredConfig($input: JammerConfigDesiredV1Input!) {
  setJammerDesiredConfig(input: $input) {
    _id
    device_id
    desired {
      message_id
      config_version
      status
      issued_at
      payload
      error
    }
    reported {
      message_id
      config_version
      status
      reported_at
      payload
      error
    }
    desired_updated_at
    reported_updated_at
  }
}
`;

const SEND_JAMMER_DEVICE_COMMAND = gql`
mutation SendJammerDeviceCommand($input: JammerCommandV1Input!) {
  sendJammerCommand(input: $input) {
    command_id
    device_id
    target_id
    target_serial_number
    control_lease_id
    control_lease_sequence
    action
    status
    attempt_count
    delivery_status
    next_attempt_at
    delivery_lease_until
    published_at
    last_delivery_error
    ack_message_id
    ack_received_at
  }
}
`;

const SET_JAMMER_DEVICE_RETIRED = gql`
mutation SetJammerDeviceRetired($deviceId: String!, $retired: Boolean!, $reason: String) {
  setJammerDeviceRetired(deviceId: $deviceId, retired: $retired, reason: $reason) {
    _id
    device_id
    lifecycle_status
    retired_at
    retired_by
    retirement_reason
    availability
    controller_state
    jamming_enabled
  }
}
`;

const JAMMER_DEVICE_COMMANDS_QUERY = gql`
query JammerDeviceCommands($clusterId: ID!, $deviceId: String!, $commandId: String, $startDate: String, $endDate: String, $limit: Int, $after: String) {
  jammerCommandsPage(clusterId: $clusterId, deviceId: $deviceId, commandId: $commandId, startDate: $startDate, endDate: $endDate, limit: $limit, after: $after) {
    items {
    _id
    command_id
    message_id
    device_id
    target_id
    target_serial_number
    control_lease_id
    control_lease_sequence
    action
    parameters
    requested_by
    reason
    issued_at
    ttl_seconds
    expires_at
    ack_timeout_ms
    max_attempts
    status
    attempt_count
    delivery_status
    next_attempt_at
    delivery_lease_until
    published_at
    last_delivery_error
    first_published_at
    last_attempt_at
    last_published_at
    ack_message_id
    ack_received_at
    ack_payload
    error
    created_at
    updated_at
    }
    nextCursor
    hasNext
  }
}
`;

const JAMMER_DEVICE_ACKS_QUERY = gql`
query JammerDeviceAcks($clusterId: ID!, $deviceId: String!, $commandId: String, $limit: Int, $after: String) {
  jammerMessagesPage(clusterId: $clusterId, deviceId: $deviceId, commandId: $commandId, messageType: "ack", limit: $limit, after: $after) {
    items {
    _id
    message_type
    direction
    device_id
    topic
    schema_version
    message_id
    command_id
    action
    controller_instance_id
    system_boot_id
    payload
    qos
    retained
    received_at
    recorded_at
    created_at
    }
    nextCursor
    hasNext
  }
}
`;

const WHITELIST_QUERY = gql`
query Query($limit: Int, $skip: Int, $status: String, $keyword: String, $startFilter: String, $endFilter: String) {
  allWhiteList(limit: $limit, skip: $skip, status: $status, keyword: $keyword, startFilter: $startFilter, endFilter: $endFilter) {
    totalCount
    data {
      _id
      name
      applyNo
      creationTime
      userName
      useDesc
      underTaker
      underTakerTel
      chargePerson
      chargePersonTel
      coordinator
      coordinatorTel
      startDate
      endDate
      startTime
      endTime
      desc
      govPurchaseNumber
      droneData {
        regNo
        type
        serialNumber
        brand
        note
        owner
        expireDate
      }
    }
  }
}
`;

const MAIN_WHITELIST_QUERY = gql`
  query MainWhiteList($time: String, $all: Boolean) {
    allWhiteList(time: $time, all: $all) {
      totalCount
      data {
        _id
        name
        applyNo
        creationTime
        userName
        useDesc
        underTaker
        underTakerTel
        chargePerson
        chargePersonTel
        coordinator
        coordinatorTel
        startDate
        endDate
        startTime
        endTime
        desc
        govPurchaseNumber
        droneData {
          type
          serialNumber
          brand
          expireDate
        }
        geoms(zoom: 12){                      # ✅ 新增
          ... on CircleGeom { kind center radius }
          ... on PolyGeom   { kind outer holes }
        }
      }
    }
  }  
`;

const MAIN_WHITELIST_UPDATED = gql`
  subscription Subscription {
    whiteListUpdated {
      _id
      name
      applyNo
      creationTime
      userName
      useDesc
      underTaker
      underTakerTel
      chargePerson
      chargePersonTel
      coordinator
      coordinatorTel
      startDate
      endDate
      startTime
      endTime
      desc
      govPurchaseNumber
      droneData {
        type
        serialNumber
        brand
        expireDate
      }
      geoms(zoom: 12){                      # ✅ 新增
        ... on CircleGeom { kind center radius }
        ... on PolyGeom   { kind outer holes }
      }
    }
  }
`;

const WHITELIST_UPDATED = gql`
  subscription Subscription {
    whiteListUpdated {
      _id
      name
      applyNo
      creationTime
      userName
      useDesc
      underTaker
      underTakerTel
      chargePerson
      chargePersonTel
      coordinator
      coordinatorTel
      startDate
      endDate
      startTime
      endTime
      desc
      govPurchaseNumber
      droneData {
        regNo
        type
        serialNumber
        brand
        note
        owner
        expireDate
      }
    }
  }
`;

const PAST_WHITELIST_QUERY = gql`
query Query($time: String, $prevHash: String, $all: Boolean) {
  pastWhiteList(time: $time, prevHash: $prevHash, all: $all) {
    unchanged
    hash
    data {
      _id
      name
      applyNo
      creationTime
      userName
      useDesc
      underTaker
      underTakerTel
      chargePerson
      chargePersonTel
      coordinator
      coordinatorTel
      startDate
      endDate
      startTime
      endTime
      desc
      govPurchaseNumber
      droneData {
        type
        serialNumber
        brand
        expireDate
      }
      geoms(zoom: 12){
        ... on CircleGeom { kind center radius }
        ... on PolyGeom   { kind outer holes }
      }
    }
  }
}
`;

const CREATE_WHITELIST = gql`
  mutation Mutation($data: WhiteListInput!) {
    createWhiteList(data: $data) {
      _id
    }
  }
`;

const REMOVE_WHITELIST = gql`
mutation Mutation($id: String!) {
  removeWhiteList(id: $id) {
    status
  }
}
`;

const BUILDING_QUERY = gql`
query BuildingData($lat: Float!, $lng: Float!, $type: String!) {
  buildingData(lat: $lat, lng: $lng, type: $type) {
    map_ver
    buildingData {
      mapNumber
      mapWidth
      mapHeight
      startPoint {
        longitude
        latitude
      }
      thirdPoint {
        latitude
        longitude
      }
      mapData
    }
    centerNumber
  }
}
`;

const NFZ_QUERY = gql`
query GetNFZ {
  getNFZ {
    location
    polygon {
      lat
      lng
    }
  }
}
`;

const UNKNOWNRECEIVE_MUTATION = gql`
mutation Mutation($status: Boolean!) {
  changeUnknownValue(status: $status) {
    clusterName
    value
  }
}
`;


const SENSORS_INFO_QUERY = gql`
query SensorsInCluster {
  sensorsInCluster {
    cluster {
      name
    }
    locationName
    tag
    apiKey
    sensorType
    _id
    sensorConfiguration {
      sensorLocation {
        latitude
        longitude
        altitude
        referenceAngle
      }
      sensorInfo {
        updatedAt
        type
        container_name
        switch_ants
        hopping_channels
        process {
          name
          startTime
          status
          cpuUsage
          memoryUsage
        }
        cpuTemperature
        coordinate {
          altitude
          latitude
          longitude
          satellite_num
        }
        fem_diag {
          connected
          error_message
          fem_name
          update_time
        }
        sa_diag {
          type
          connected
          error_message
          last_mission_status
          last_mission_time
          temperature
          update_time
        }
        process_stats {
          burst_queue_size
          is_connected
          last_update
          process_queue_size
          process_start_time
          processing_rate
          receive_queue_size
          total_processed
          update_time
        }
        system_resources {
          cpu_percent
          memory_percent
          ssd_used_gb
          ssd_total_gb
          ssd_percent
          update_time
        }
        errMessage
      }
    }
    monitorStatus {
      ...MonitorStatusSummary
    }
  }
}
  ${MONITOR_STATUS_SUMMARY}
`;

const SENSOR_INFO_UPDATED = gql`
subscription Subscription {
  sensorInfoUpdated {
    sensor {
      _id
    }
    updatedAt
    type
    container_name
    switch_ants
    hopping_channels
    process {
      name
      startTime
      status
      cpuUsage
      memoryUsage
    }
    cpuTemperature
    coordinate {
      altitude
      latitude
      longitude
      satellite_num
    }
    fem_diag {
      connected
      error_message
      fem_name
      update_time
    }
    sa_diag {
      type
      connected
      error_message
      last_mission_status
      last_mission_time
      temperature
      update_time
    }
    process_stats {
      burst_queue_size
      is_connected
      last_update
      process_queue_size
      process_start_time
      processing_rate
      receive_queue_size
      total_processed
      update_time
    }
    system_resources {
      cpu_percent
      memory_percent
      ssd_used_gb
      ssd_total_gb
      ssd_percent
      update_time
    }
    errMessage
  }
}
`;

const MONITOR_STATUS_UPDATED = gql`
  subscription MonitorStatusUpdated {
    monitorStatusUpdated {
      ...MonitorStatusSummary
    }
  }
  ${MONITOR_STATUS_SUMMARY}
`;

const SENSOR_CHECK_UPDATED = gql`
subscription Subscription {
  sensorCheckUpdated {
    status
    commit
    apiKey
  }
}
`;

const MUTATION_SENSOR_UPDATED = gql`
mutation Mutation($apiKey: String!) {
  checkSensorUpdated(apiKey: $apiKey)
}
`;

const SETTING_UPDATED_SUBSCRIBE = gql`
subscription Subscription {
  settingUpdated {
    value
    valueName
  }
}
`

const SET_SENSOR_ANT_AND_HOPPING_CHANNEL = gql`
mutation Mutation($apiKey: String!, $hoppingChannels: [Float]!, $switchAnts: [Int]!) {
  setSensorAntAndHoppingChannel(apiKey: $apiKey, hopping_channels: $hoppingChannels, switch_ants: $switchAnts)
}
`;

const GET_AUDIT_LOGS = gql`
  query GetAuditLogs($limit: Int, $after: String, $action: String, $keyword: String) {
    getAuditLogPage(limit: $limit, after: $after, action: $action, keyword: $keyword) {
      items {
        _id
        action
        metadata
        user
        ipAddress
        created_at
      }
      nextCursor
      hasNext
    }
  }
`;

const AUDIT_LOG_UPDATED = gql`
subscription Subscription {
  auditLogUpdated {
    _id
    action
    metadata
    user
    ipAddress
    created_at
  }
}
`;

const SAMPLE_ALONG_RAY_QUERY = gql`
  query SampleAlongRay(
    $startLat: Float!,
    $startLng: Float!,
    $targetLat: Float!,
    $targetLng: Float!,
    $maxDistM: Int,
    $stepM: Int,
  ) {
    sampleAlongRay(
      startLat: $startLat,
      startLng: $startLng,
      targetLat: $targetLat,
      targetLng: $targetLng,
      maxDistM: $maxDistM,
      stepM: $stepM,
    ) {
      angle
      distanceM
      hitLat
      hitLng
    }
  }
`;

const USERS_PERMS_QUERY = gql`
  query UsersForPerms {
    users {
      id
      name
      email
      authority
      verified
      permGrants {
        key
        allow
      }
    }
  }
`;

const SET_USER_PERM_GRANTS = gql`
  mutation SetUserPermGrants($userId: ID!, $grants: [PermGrantInput!]!) {
    setUserPermGrants(userId: $userId, grants: $grants)
  }
`;

const USER_PERM_GRANTS_UPDATED = gql`
  subscription Subscription {
    userPermGrantsUpdated {
      id
      permGrants {
        key
        allow
      }
      authority
      verified
    }
  }
`;

const GET_PERMISSIONS_KEYS = gql`
  query GetPermissionsKeys {
    getPermissionsKeys
  }
`;

const SENSOR_SIGN_UP = gql`
mutation SensorSignUp(
  $tag:String!, $clusterName:String!, $sensorType:String!,
  $locationName:String, $show:Boolean
){
  sensorSignUp(
    tag:$tag, clusterName:$clusterName, sensorType:$sensorType,
    locationName:$locationName, show:$show
  ){
    _id
    tag
    sensorType
    locationName
    show
    cluster { name }
  }
}
`;

const USERS_QUERY = gql`
  query Users {
    users {
      id
      name
      email
      authority
      verified
      verifiedEmailNorm
      clusters {
        _id
        name
      }
      cluster {
        _id
        name
      }
    }
  }
`;

const SET_USER_AUTHORITY = gql`
  mutation SetUserAuthority($userId: ID!, $authority: String!) {
    setUserAuthority(userId: $userId, authority: $authority)
  }
`;

const ADMIN_RESET_VERIFY_EMAIL = gql`
  mutation AdminResetEmailVerification($userId: ID!) {
    adminResetEmailVerification(userId: $userId)
  }
`;

const ADMIN_STEP_UP = gql`
  mutation AdminStepUp($password: String!) {
    adminStepUp(password: $password)
  }
`;

const ADMIN_RESET_PASSWORD = gql`
  mutation AdminResetPassword($userId: ID!, $newPassword: String!, $stepUpToken: String!) {
    adminResetPassword(userId: $userId, newPassword: $newPassword, stepUpToken: $stepUpToken)
  }
`;

const CHANGE_PASSWORD_WITH_OLD = gql`
  mutation ChangePasswordWithOld($userId: ID!, $oldPassword: String!, $newPassword: String!) {
    changePasswordWithOld(userId: $userId, oldPassword: $oldPassword, newPassword: $newPassword)
  }
`;

const ADMIN_DELETE_USER = gql`
  mutation AdminDeleteUser($userId: ID!) {
    adminDeleteUser(userId: $userId)
  }
`;

const SIGN_UP = gql`
  mutation SignUp(
    $name: String
    $email: String!
    $password: String!
    $clusterName: String!
    $authority: String!
  ) {
    signUp(
      name: $name
      email: $email
      password: $password
      clusterName: $clusterName
      authority: $authority
    ) {
      id
      name
      email
      authority
      verified
    }
  }
`;

const ROLES_QUERY = gql`
  query Roles {
    roles {
      key
      name
      permissions
    }
  }
`;

const SET_ROLE_PERMISSIONS = gql`
  mutation SetRolePermissions($roleKey: String!, $permissions: [String!]!) {
    setRolePermissions(roleKey: $roleKey, permissions: $permissions)
  }
`;

const USER_LOGIN_LOGS = gql`
  query UserLoginLogs($email: String!, $limit: Int) {
    user(email: $email) {
      id
      loginLogs(limit: $limit) {
        _id
        ipAddress
        metadata
        created_at
      }
    }
  }
`;

const DELETE_TARGET_REQUEST = gql`
mutation Mutation($deleteTargetRequsetId: String!) {
  deleteTargetRequset(id: $deleteTargetRequsetId)
}
`

const GET_API_KEY = gql`
query Query($sensorId: ID!) {
  getApiKey(sensorId: $sensorId)
}
`;

const QUERY_HEATMAP = gql`
query GetHeatmap($time: String!) {
  getHeatmap(time: $time) {
    points {
      lat
      lng
      weight
    }
    timestamp
  }
}
`;

const HEATMAP_UPDATED = gql`
subscription Subscription {
  heatmapUpdated {
    action
    points {
      lat
      lng
      weight
    }
    timestamp
  }
}
`;

const GET_RELAY_STATUS = gql`
  query GetRelayStatus {
    getRelayStatus {
      _id
      altModEnabled
      suppressEnabled
      jitterEnabled
      altOverrideValue
      minAltThreshold
      maxJitterUnits
      mapping {
        oldType
        newType
        oldSn
        newSn
        altOverride
        altThreshold
      }
      created_at
    }
  }
`;

const COMMAND_RELAY = gql`
  mutation CommandRelay($data: commandRelayInput) {
    commandRelay(data: $data)
  }
`;

const RELAY_UPDATED = gql`
  subscription RelayUpdated {
    relayUpdated {
      _id
      altModEnabled
      suppressEnabled
      jitterEnabled
      altOverrideValue
      minAltThreshold
      maxJitterUnits
      mapping {
        oldType
        newType
        oldSn
        newSn
        altOverride
        altThreshold
      }
      created_at
    }
  }
`;

export {
  TARGETS_QUERY,
  TARGETS_QUERY_BY_ID,
  TARGETS_SUBSCRIPTION,
  PAST_TARGET,
  TARGET_HISTORY_BY_ID,
  LOCATIONS,
  SENSOR_UPDATED,
  SENSORS_QUERY,
  POLL_PLAYBACK_TARGET_WITH_TIME,
  COMMAND_SENSOR,
  UPDATE_SENSOR_CONFIGURATION,
  DELETE_SENSOR,
  UPDATE_TARGET_ARBITRARY,
  CREATE_TARGET_ARBITRARY,
  TOGGLE_TARGET_LOCKED,
  LOGIN_MUTATION,
  ME,
  CHANGE_MAIN_CLUSTER,
  UPDATE_USER_CLUSTERS,
  REQUEST_EMAIL_VERIFICATION,
  VERIFY_EMAIL,
  CLUSTER_QUERY,
  ALL_CLUSTER_QUERY,
  POLICESTATION_QUERY,
  ADMIN_POLICE_STATIONS_QUERY,
  CREATE_POLICE_STATION,
  UPDATE_POLICE_STATION,
  DELETE_POLICE_STATION,
  BOUNDARY_QUERY,
  ADMIN_BOUNDARIES_QUERY,
  CREATE_CLUSTER,
  UPDATE_CLUSTER_CENTER,
  UPDATE_PRESENCE_LOG_EXPORT_SETTINGS,
  CREATE_BOUNDARY,
  UPDATE_BOUNDARY,
  SET_BOUNDARY_ENABLED,
  DELETE_BOUNDARY,
  SENSOR_MODE_SENSORS_QUERY,
  SENSOR_MODES_QUERY,
  SENSOR_MODE_EXECUTIONS_QUERY,
  CREATE_SENSOR_MODE,
  UPDATE_SENSOR_MODE,
  DUPLICATE_SENSOR_MODE,
  SET_SENSOR_MODE_ENABLED,
  DELETE_SENSOR_MODE,
  EXECUTE_SENSOR_MODE,
  EXECUTE_SENSOR_MODES,
  GET_SETTING,
  GET_PRESENCE_EXPORT_SETTING,
  UPDATE_BASESTATION_MONITOR_SETTINGS,
  CHANGE_ONLYDEVELOPMENT,
  JAMMER_QUERY,
  JAMMER_SUBSCRIPTION,
  JAMMER_CHECK_UPDATE,
  JAMMER_DEVICE_QUERY,
  JAMMER_DEVICE_SUBSCRIPTION,
  REGISTER_JAMMER_DEVICE,
  JAMMER_CONFIG_QUERY,
  SET_JAMMER_DESIRED_CONFIG,
  SEND_JAMMER_DEVICE_COMMAND,
  SET_JAMMER_DEVICE_RETIRED,
  JAMMER_DEVICE_COMMANDS_QUERY,
  JAMMER_DEVICE_ACKS_QUERY,
  WHITELIST_QUERY,
  WHITELIST_UPDATED,
  CREATE_WHITELIST,
  REMOVE_WHITELIST,
  BUILDING_QUERY,
  NFZ_QUERY,
  UNKNOWNRECEIVE_MUTATION,
  SENSORS_INFO_QUERY,
  SENSOR_INFO_UPDATED,
  MONITOR_STATUS_UPDATED,
  SENSOR_CHECK_UPDATED,
  MUTATION_SENSOR_UPDATED,
  SETTING_UPDATED_SUBSCRIBE,
  DELETE_TARGET_REQUEST,
  GET_PAST_TARGETS_LIST,
  GET_API_KEY,
  SET_SENSOR_ANT_AND_HOPPING_CHANNEL,
  MAIN_WHITELIST_QUERY,
  MAIN_WHITELIST_UPDATED,
  PAST_WHITELIST_QUERY,
  GET_AUDIT_LOGS,
  AUDIT_LOG_UPDATED,
  LOGOUT,
  SAMPLE_ALONG_RAY_QUERY,
  USERS_PERMS_QUERY,
  SET_USER_PERM_GRANTS,
  USER_PERM_GRANTS_UPDATED,
  GET_PERMISSIONS_KEYS,
  SENSOR_SIGN_UP,
  USERS_QUERY,
  SET_USER_AUTHORITY,
  ADMIN_RESET_VERIFY_EMAIL,
  ADMIN_STEP_UP,
  ADMIN_RESET_PASSWORD,
  CHANGE_PASSWORD_WITH_OLD,
  ADMIN_DELETE_USER,
  SIGN_UP,
  ROLES_QUERY,
  SET_ROLE_PERMISSIONS,
  USER_LOGIN_LOGS,
  QUERY_HEATMAP,
  HEATMAP_UPDATED,
  RELAY_UPDATED,
  COMMAND_RELAY,
  GET_RELAY_STATUS
};
